import { useCallback, useRef, useState } from 'react';

import type { ChartV2Horizon, ChartV2Interval } from '../types/chart-v2.types';

export interface UseHorizonIntervalOptions {
  /** `null` = controlled custom domain (no chip selected), matching v1. */
  controlledHorizon?: ChartV2Horizon | null;
  controlledInterval?: ChartV2Interval;
  defaultHorizon: ChartV2Horizon;
  defaultInterval: ChartV2Interval;
  onHorizonChange?: (horizon: ChartV2Horizon) => void;
  onIntervalChange?: (interval: ChartV2Interval) => void;
}

export interface UseHorizonIntervalResult {
  horizon: ChartV2Horizon | null;
  /**
   * Last non-null horizon, falling back to `defaultHorizon`. The window
   * (and reset) needs a horizon even while the chip is deselected.
   */
  lastHorizon: ChartV2Horizon;
  interval: ChartV2Interval;
  handleHorizonChange: (next: ChartV2Horizon) => void;
  handleIntervalChange: (next: ChartV2Interval) => void;
}

/**
 * Standard controlled / uncontrolled pair for horizon + interval. When a
 * controlled value is provided it wins (`null` deselects); otherwise the
 * hook manages internal state and the `handle*` callbacks both update
 * state and emit the optional consumer callback.
 */
export const useHorizonInterval = ({
  controlledHorizon,
  controlledInterval,
  defaultHorizon,
  defaultInterval,
  onHorizonChange,
  onIntervalChange,
}: UseHorizonIntervalOptions): UseHorizonIntervalResult => {
  const [internalHorizon, setInternalHorizon] = useState<ChartV2Horizon>(
    controlledHorizon ?? defaultHorizon
  );
  const [internalInterval, setInternalInterval] = useState<ChartV2Interval>(
    controlledInterval ?? defaultInterval
  );

  // `undefined` = uncontrolled; `null` = controlled with no selection.
  const horizon =
    controlledHorizon !== undefined ? controlledHorizon : internalHorizon;
  const interval = controlledInterval ?? internalInterval;

  const lastHorizonRef = useRef<ChartV2Horizon>(horizon ?? defaultHorizon);

  if (horizon !== null) {
    lastHorizonRef.current = horizon;
  }

  const handleHorizonChange = useCallback(
    (next: ChartV2Horizon) => {
      if (controlledHorizon === undefined) {
        setInternalHorizon(next);
      }

      onHorizonChange?.(next);
    },
    [controlledHorizon, onHorizonChange]
  );

  const handleIntervalChange = useCallback(
    (next: ChartV2Interval) => {
      if (controlledInterval === undefined) {
        setInternalInterval(next);
      }

      onIntervalChange?.(next);
    },
    [controlledInterval, onIntervalChange]
  );

  return {
    horizon,
    lastHorizon: lastHorizonRef.current,
    interval,
    handleHorizonChange,
    handleIntervalChange,
  };
};
